import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalConnectionDialogResult } from '../../../core/services/connection-monitor.service';

export interface GlobalConnectionDialogData {
  message: string;
  showRetry: boolean;
  showReload: boolean;
}

@Component({
  selector: 'app-global-connection-dialog',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, TranslateModule],
  standalone: true,
  template: `
    <div class="global-connection-dialog">
      <div class="dialog-header">
        <mat-icon class="connection-icon">warning</mat-icon>
        <h2 mat-dialog-title>{{ 'ui.connection.globalTitleLost' | translate }}</h2>
      </div>
      
      <mat-dialog-content>
        <p class="dialog-message">
          {{ (data.message | translate) === data.message ? data.message : (data.message | translate) }}
        </p>
        
        <div class="connection-info">
          <mat-icon>info</mat-icon>
          <span>{{ 'ui.connection.globalSessionHint' | translate }}</span>
        </div>
      </mat-dialog-content>
      
      <mat-dialog-actions align="end" class="dialog-actions">
        <button
          mat-button 
          *ngIf="data.showRetry"
          (click)="onRetry()"
          color="primary">
          <mat-icon>refresh</mat-icon>
          {{ 'ui.connection.tryAgain' | translate }}
        </button>
        
        <button
          mat-flat-button 
          *ngIf="data.showReload"
          (click)="onReload()"
          color="warn">
          <mat-icon>replay</mat-icon>
          {{ 'ui.connection.reloadPage' | translate }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .global-connection-dialog {
      padding: 24px;
      border-radius: 8px;
      overflow: hidden;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 400px;
      max-width: 500px;
    }
    
    .dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-bottom: 16px;
      margin-bottom: 16px;
      border-bottom: 1px solid #eee;
    }
    
    .connection-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: #ff9800; /* Amber for warning */
    }
    
    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #333;
    }
    
    .dialog-message {
      font-size: 16px;
      line-height: 1.5;
      color: #555;
      margin-bottom: 20px;
    }
    
    .connection-info {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background-color: #f5f5f5;
      border-radius: 6px;
      font-size: 14px;
      color: #666;
    }
    
    .connection-info mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      color: #2196f3;
      margin-top: 2px;
    }
    
    mat-dialog-actions {
      padding-top: 20px;
      border-top: 1px solid #eee;
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
    
    mat-dialog-actions button {
      min-width: 120px;
      padding: 10px 20px;
      font-size: 14px;
      font-weight: 500;
    }
    
    /* Avoid LTR-only spacing (works in RTL too via flex gap) */
    mat-dialog-actions button + button { margin-left: 0; }

    /* RTL: keep button content readable, avoid cramped icon+text */
    :host-context([dir="rtl"]) mat-dialog-actions button {
      direction: rtl;
    }

    :host-context([dir="rtl"]) mat-dialog-actions button .mat-icon {
      margin-left: 8px;
      margin-right: 0;
    }
    
    /* Mobile responsiveness */
    @media (max-width: 600px) {
      .global-connection-dialog {
        min-width: 300px;
        margin: 16px;
      }
      
      h2 {
        font-size: 20px;
      }
      
      .dialog-message {
        font-size: 15px;
      }
      
      mat-dialog-actions button {
        min-width: 100px;
        padding: 8px 16px;
      }
    }
  `]
})
export class GlobalConnectionDialogComponent {
  
  constructor(
    public dialogRef: MatDialogRef<GlobalConnectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GlobalConnectionDialogData
  ) {}

  onReload(): void {
    this.dialogRef.close({ action: 'reload' } as GlobalConnectionDialogResult);
  }

  onRetry(): void {
    this.dialogRef.close({ action: 'retry' } as GlobalConnectionDialogResult);
  }
}
